import { companyHeaderStyles } from "../../styles/shared/companyHeaderStyles";
import React, { useMemo } from "react";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { FilterAltOutlined as FilterAltOutlinedIcon } from "../icons/FigmaControlIcons";

import InfoActionIcons from "../InfoActionIcons";
import {
  buildCompanyProfileActions,
  getPrimaryCompanyWebsite } from "../../utils/companySocialLinks";
import {
  withPillButtonClassName } from "../../utils/pillButtonStyles";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const CONTACT_ACTION_TYPES = new Set(["website", "email", "phone"]);
const SOCIAL_ACTION_TYPES = new Set([
  "linkedin",
  "facebook",
  "twitter",
  "x",
  "google",
  "instagram",
  "youtube",
  "tiktok",
]);

const resolveAssetUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/uploads/") || raw.startsWith("uploads/")) {
    const normalized = raw.startsWith("/") ? raw : `/${raw}`;
    return `${API_BASE}${normalized}`;
  }
  return raw;
};

const formatWebsiteLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./i, "");
  } catch {
    return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "");
  }
};

export function CompanyTabSummaryRow({ company = {}, onFilterClick }) {
  const companyName = String(company?.name || company?.company_name || "Company").trim();
  const companyLogo = resolveAssetUrl(company?.logo || company?.company_logo || "");
  const companyWebsite = formatWebsiteLabel(getPrimaryCompanyWebsite(company) || "");

  const companyActions = useMemo(() => {
    const fallbackActions = buildCompanyProfileActions(company);

    return [
      { type: "website", href: getPrimaryCompanyWebsite(company) || "" },
      {
        type: "email",
        href: company?.contact_email || company?.company_email || company?.email || "" },
      {
        type: "phone",
        href: company?.contact_phone || company?.company_phone || company?.phone || "" },
      ...fallbackActions.filter((action) => SOCIAL_ACTION_TYPES.has(action?.type)),
    ];
  }, [company]);
  const contactActions = useMemo(
    () => companyActions.filter((action) => CONTACT_ACTION_TYPES.has(action?.type)),
    [companyActions]
  );
  const socialActions = useMemo(
    () => companyActions.filter((action) => SOCIAL_ACTION_TYPES.has(action?.type)),
    [companyActions]
  );

  return (
    <>
      <Box
        sx={companyHeaderStyles.root()}
      >
        <Box
          sx={companyHeaderStyles.identity()}
        >
          <Avatar
            src={companyLogo || undefined}
            sx={companyHeaderStyles.avatar({ companyLogo })}
          >
            {companyName[0] || "C"}
          </Avatar>

          <Box sx={companyHeaderStyles.text()}>
            <Typography
              sx={companyHeaderStyles.name()}
            >
              {companyName}
            </Typography>
            <Typography
              sx={companyHeaderStyles.website()}
            >
              {companyWebsite || "-"}
            </Typography>
          </Box>

          <Box
            aria-hidden="true"
            sx={companyHeaderStyles.contactDivider()}
          />

          <Box
            sx={companyHeaderStyles.contacts()}
          >
            <InfoActionIcons
              actions={contactActions}
              gap={1.1}
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
            />

            {contactActions.length && socialActions.length ? (
              <Box
                aria-hidden="true"
                sx={companyHeaderStyles.socialDivider()}
              />
            ) : null}

            <InfoActionIcons
              actions={socialActions}
              gap={1.1}
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
            />
          </Box>
        </Box>

        <Button
          variant="contained"
          className={withPillButtonClassName()}
          onClick={onFilterClick}
          startIcon={<FilterAltOutlinedIcon sx={companyHeaderStyles.filterIcon()} />}
          sx={companyHeaderStyles.filterButton()}
        >
          Filter By
        </Button>
      </Box>
    </>
  );
}

export function CompanyTabSectionHeader({ title, actions = [] }) {
  return (
    <Box
      sx={companyHeaderStyles.section()}
    >
      <Box
        sx={companyHeaderStyles.sectionRow()}
      >
        <Typography
          sx={companyHeaderStyles.sectionTitle()}
        >
          {title}
        </Typography>

        {actions.length ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {actions.map((action, index) => (
              <React.Fragment key={`${action?.label || "action"}-${index}`}>
                {index > 0 ? (
                  <Typography sx={companyHeaderStyles.actionDivider()}>|</Typography>
                ) : null}
                <Typography
                  component="button"
                  type="button"
                  onClick={action?.onClick}
                  sx={companyHeaderStyles.sectionAction({ action })}
                >
                  {action?.label || ""}
                </Typography>
              </React.Fragment>
            ))}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}

export { TAB_BLUE, TAB_PURPLE, TAB_LIGHT_BLUE, TAB_BORDER } from "../../styles/shared/companyHeaderStyles";
